import { useState, useMemo, useEffect } from "react";
import {
  Stack,
  Button,
  Group,
  Paper,
  Text,
  ActionIcon,
  Modal,
  Divider,
  Loader,
} from "@mantine/core";
import { IconEdit } from "@tabler/icons-react";
import moment from "moment-timezone";
import { useSalon } from "../../../../../context/SalonContext";
import { useCalendar } from "../../../../../context/CalendarContext";
import { useMediaQuery } from "@mantine/hooks";
import styles from "./MobileAppointmentForm.module.css";
import DateTimeSection from "./sections/DateTimeSection";
import StaffSection from "./sections/StaffSection";
import ServiceSection from "./sections/ServiceSection";
import DurationSection from "./sections/DurationSection";
import ClientSection from "./sections/ClientSection";
import { useClient } from "../../../../../context/ClientContext";
import { formatPhoneNumber } from "../../../../../utils/phoneUtils";
import {
  calculateDurationAndEndTime,
  calculateEndTime,
  handleSubmitHelper,
  hourOptions,
} from "../../../../../utils/appointmentFormUtils";
import { filterBookableActiveStaff } from "../../../../../utils/staffUtils";

function MobileAppointmentForm({ initialData, onSubmit, isLoading = false }) {
  const { employees, services, businessInfo } = useSalon();
  const { staffServices, selectedDate } = useCalendar();
  const [availableServices, setAvailableServices] = useState(services);

  const [availableStaff, setAvailableStaff] = useState(() =>
    filterBookableActiveStaff(employees, initialData?.employeeId),
  );

  const { clientsById } = useClient();
  const isMobile = useMediaQuery("(max-width: 768px)");

  // Helper function to initialize form data
  const initializeFormData = (data) => {
    // Check if data is a SlotInfo object (from calendar slot selection)
    const isSlotInfo = data?.start instanceof Date && data?.action;

    let startTime = "";
    let endTime = "";
    let date = selectedDate;
    let employeeId = "";

    if (isSlotInfo) {
      // Extract time and date from SlotInfo
      startTime = moment(data.start).format("HH:mm");
      date = data.start;
      employeeId = data.resourceId || "";
    } else {
      // Use data properties for other cases
      startTime = data?.startTime || "";
      endTime = data?.endTime || "";
      date = data?.selectedDate || selectedDate;
      employeeId = data?.employeeId || "";
    }

    return {
      salonServiceId: data?.salonServiceId || "",
      employeeId: employeeId,
      startTime: startTime,
      endTime: endTime,
      date: date,
      baseDuration: data?.baseDuration ?? 30,
      processingTime: data?.processingTime ?? 0,
      finishingTime: data?.finishingTime ?? 0,
      bufferTime: data?.bufferTime ?? 0,
      clientUuid: data?.clientUuid || "",
    };
  };

  // Form state
  const [formData, setFormData] = useState(initializeFormData(initialData));

  // Section navigation state
  const [activeSection, setActiveSection] = useState(null);
  const [errors, setErrors] = useState({});

  // Update available staff when employees load or change
  useEffect(() => {
    if (!formData.salonServiceId) {
      // Only update if no service is selected (service selection has its own filtering)
      setAvailableStaff(
        filterBookableActiveStaff(employees, formData.employeeId),
      );
    }
  }, [employees]);

  // Filter services when form initializes with a pre-selected staff member
  useEffect(() => {
    if (formData.employeeId && staffServices && services) {
      const staffServiceIds =
        staffServices
          .filter((service) => service.staffId === formData.employeeId)
          ?.map((service) => service.salonServiceId) || [];

      const filteredServices = services.filter((service) =>
        staffServiceIds.includes(service.id),
      );

      setAvailableServices(filteredServices);
    } else if (services) {
      // Reset to all services if no staff selected
      setAvailableServices(services);
    }
  }, [formData.employeeId, staffServices, services]);

  useEffect(() => {
    setFormData(initializeFormData(initialData));
  }, [initialData, selectedDate]);

  // Filter staff when form initializes with a pre-selected service
  useEffect(() => {
    if (formData.salonServiceId && services) {
      const selectedService = services.find(
        (s) => s.id === formData.salonServiceId,
      );

      if (selectedService) {
        const availableStaffIds =
          selectedService.servicing_staff?.map((staff) => staff.staff_id) || [];
        const filteredStaff = filterBookableActiveStaff(
          employees,
          formData.employeeId,
        ).filter((emp) => availableStaffIds.includes(emp.id));

        setAvailableStaff(filteredStaff);
      }
    } else {
      // Reset to active staff (plus currently selected) if no service selected
      setAvailableStaff(
        filterBookableActiveStaff(employees, formData.employeeId),
      );
    }
  }, [formData.salonServiceId, formData.employeeId, services, employees]);

  const getUpdatedDurationAndEndTime = (staffId, serviceId) => {
    const calculated = calculateDurationAndEndTime(
      services,
      staffId,
      serviceId,
      formData.startTime,
      formData,
    );

    // Return default values if staff doesn't offer this service
    if (!calculated) {
      return {
        baseDuration: 30,
        processingTime: 0,
        finishingTime: 0,
        bufferTime: 0,
        endTime: formData.startTime
          ? moment(formData.startTime, "HH:mm")
              .add(30, "minutes")
              .format("HH:mm")
          : "",
      };
    }

    return {
      baseDuration: calculated.baseDuration,
      processingTime: calculated.processingTime,
      finishingTime: calculated.finishingTime,
      bufferTime: calculated.bufferTime,
      endTime: calculated.endTime,
    };
  };

  // Handle form data updates
  const handleFormDataChange = (updates) => {
    setFormData((prev) => {
      const newData = { ...prev, ...updates };

      // Recalculate end time if durations or start time changed
      if (
        newData.startTime &&
        (updates.baseDuration !== undefined ||
          updates.processingTime !== undefined ||
          updates.finishingTime !== undefined ||
          updates.bufferTime !== undefined ||
          updates.startTime !== undefined)
      ) {
        const totalMinutes =
          (newData.baseDuration ?? 0) +
          (newData.processingTime ?? 0) +
          (newData.finishingTime ?? 0) +
          (newData.bufferTime ?? 0);

        newData.endTime = moment(newData.startTime, "HH:mm")
          .add(totalMinutes, "minutes")
          .format("HH:mm");
      }

      if (updates.employeeId !== undefined) {
        handleStaffChange(updates.employeeId);
      }

      if (updates.salonServiceId !== undefined) {
        handleServiceChange(updates.salonServiceId);

        if (formData.employeeId) {
          const calculated = getUpdatedDurationAndEndTime(
            formData.employeeId,
            updates.salonServiceId,
          );
          if (calculated) {
            newData.baseDuration = calculated.baseDuration;
            newData.processingTime = calculated.processingTime;
            newData.finishingTime = calculated.finishingTime;
            newData.bufferTime = calculated.bufferTime;
            newData.endTime = calculated.endTime;
          }
        }
      }

      return newData;
    });
  };

  const handleStaffChange = (staffId) => {
    if (staffId) {
      const staffServiceIds = staffServices
        .filter((service) => service.staffId === staffId)
        .map((service) => service.salonServiceId);

      const filteredServices = services.filter((service) =>
        staffServiceIds.includes(service.id),
      );

      setAvailableServices(filteredServices);
      setErrors((prev) => ({ ...prev, employeeId: "" }));
    } else {
      setAvailableServices(services);
    }
  };

  const handleServiceChange = (serviceId) => {
    if (serviceId) {
      const selectedService = services.find((s) => s.id === serviceId);

      if (selectedService) {
        const availableStaffIds = selectedService.servicing_staff.map(
          (staff) => staff.staff_id,
        );
        const filteredStaff = filterBookableActiveStaff(
          employees,
          formData.employeeId,
        ).filter((emp) => availableStaffIds.includes(emp.id));

        setAvailableStaff(filteredStaff);
        setErrors((prev) => ({ ...prev, salonServiceId: "" }));
      }
    } else {
      setAvailableStaff(
        filterBookableActiveStaff(employees, formData.employeeId),
      );
    }
  };

  // Section navigation
  const openSection = (section) => {
    setActiveSection(section);
  };

  const closeSection = () => {
    setActiveSection(null);
  };

  // Format current values for display
  const formatDateTimeValue = () => {
    if (!formData.date) return "-";

    const dateStr = moment(formData.date).format("MMM D, YYYY");
    if (!formData.startTime) return dateStr;

    const startTime = moment(formData.startTime, "HH:mm").format("h:mm A");

    if (formData.endTime) {
      const endTime = moment(formData.endTime, "HH:mm").format("h:mm A");
      return (
        <>
          <Text size="md">{dateStr}</Text>
          <Text size="md">
            {startTime} - {endTime}
          </Text>
        </>
      );
    }
    return (
      <>
        <Text size="md">{dateStr}</Text>
        <Text size="md">{startTime}</Text>
      </>
    );
  };

  const formatStaffValue = () => {
    if (!formData.employeeId) return "-";
    const staff = employees.find((emp) => emp.id === formData.employeeId);
    return staff
      ? `${staff.first_name} ${staff.last_name} - ${staff.title}`
      : "-";
  };

  // Name of the staff member the service list is filtered by, used to explain
  // empty search results in ServiceSection.
  const selectedStaffName = useMemo(() => {
    if (!formData.employeeId) return "";
    const staff = employees?.find((emp) => emp.id === formData.employeeId);
    return staff ? `${staff.first_name} ${staff.last_name}` : "";
  }, [employees, formData.employeeId]);

  const formatServiceValue = () => {
    if (!formData.salonServiceId) return "-";
    const service = services.find((s) => s.id === formData.salonServiceId);
    if (!service) return "-";

    return `${service.name} - $${service.default_price}`;
  };

  const formatClientValue = () => {
    if (!formData.clientUuid) return "-";
    const client = clientsById[formData.clientUuid];
    return (
      <>
        <Text size="md">
          {client?.first_name} {client?.last_name}
        </Text>
        <Text size="md">
          {client &&
            formatPhoneNumber(`${client?.area_code}${client?.phone_number}`)}
        </Text>
        <Text size="md">{client?.email}</Text>
      </>
    );
  };

  const formatDurationValue = () => {
    const totalMinutes =
      (formData.baseDuration ?? 0) +
      (formData.processingTime ?? 0) +
      (formData.finishingTime ?? 0) +
      (formData.bufferTime ?? 0);

    if (totalMinutes === 0) return "-";

    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;

    let result = "";
    if (hours > 0) {
      result += `${hours}hr`;
    }
    if (minutes > 0) {
      if (result) result += " ";
      result += `${minutes}min`;
    }
    return result;
  };

  // Validation
  const validateSection = (section) => {
    const newErrors = { ...errors };

    switch (section) {
      case "datetime":
        if (!formData.date) newErrors.date = "Date is required";
        else delete newErrors.date;
        if (!formData.startTime) newErrors.startTime = "Start time is required";
        else delete newErrors.startTime;
        break;
      case "staff":
        if (!formData.employeeId) newErrors.employeeId = "Staff is required";
        else delete newErrors.employeeId;
        break;
      case "service":
        if (!formData.salonServiceId)
          newErrors.salonServiceId = "Service is required";
        else delete newErrors.salonServiceId;
        break;
      case "duration":
        const totalMinutes =
          (formData.baseDuration ?? 0) +
          (formData.processingTime ?? 0) +
          (formData.finishingTime ?? 0) +
          (formData.bufferTime ?? 0);
        if (totalMinutes === 0) newErrors.duration = "Duration is required";
        else delete newErrors.duration;
        break;
      case "client":
        if (!formData.clientUuid) newErrors.clientUuid = "Client is required";
        else delete newErrors.clientUuid;
        break;
    }

    setErrors(newErrors);
    return !Object.values(newErrors).some((error) => error !== "");
  };

  const validateForm = () => {
    const totalMinutes =
      (formData.baseDuration ?? 0) +
      (formData.processingTime ?? 0) +
      (formData.finishingTime ?? 0) +
      (formData.bufferTime ?? 0);

    const newErrors = {
      date: !formData.date ? "Date is required" : "",
      startTime: !formData.startTime ? "Start time is required" : "",
      employeeId: !formData.employeeId ? "Staff is required" : "",
      salonServiceId: !formData.salonServiceId ? "Service is required" : "",
      clientUuid: !formData.clientUuid ? "Client is required" : "",
      duration: totalMinutes === 0 ? "Duration is required" : "",
    };

    setErrors(newErrors);
    return !Object.values(newErrors).some((error) => error !== "");
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    const appointmentData = handleSubmitHelper(
      formData,
      staffServices,
      services,
      [],
      businessInfo?.timezone,
    );
    onSubmit(appointmentData);
  };

  // Don't render on desktop
  if (!isMobile) {
    return null;
  }

  return (
    <>
      <Paper className={styles.mobileAppointmentForm}>
        <Stack gap={0}>
          {/* Date & Time Section */}
          <Group
            position="apart"
            className={styles.formSectionRow}
            onClick={() => openSection("datetime")}
          >
            <Stack gap={4} style={{ flex: 1 }}>
              <Group gap={4}>
                <Text size="sm" c="dimmed">
                  Date & Time
                </Text>
                <Text size="sm" c="red">
                  *
                </Text>
              </Group>
              {formatDateTimeValue()}
              {(errors.date || errors.startTime) && (
                <Text size="xs" c="red">
                  {errors.date || errors.startTime}
                </Text>
              )}
            </Stack>
            <ActionIcon variant="transparent" style={{ marginLeft: "auto" }}>
              <IconEdit size={16} />
            </ActionIcon>
          </Group>

          {/* Staff Section */}
          <Group
            position="apart"
            className={styles.formSectionRow}
            onClick={() => openSection("staff")}
          >
            <Stack gap={4} style={{ flex: 1 }}>
              <Group gap={4}>
                <Text size="sm" c="dimmed">
                  Staff
                </Text>
                <Text size="sm" c="red">
                  *
                </Text>
              </Group>
              <Text size="md">{formatStaffValue()}</Text>
              {errors.employeeId && (
                <Text size="xs" c="red">
                  {errors.employeeId}
                </Text>
              )}
            </Stack>
            <ActionIcon variant="transparent" style={{ marginLeft: "auto" }}>
              <IconEdit size={16} />
            </ActionIcon>
          </Group>

          {/* Service Section */}
          <Group
            position="apart"
            className={styles.formSectionRow}
            onClick={() => openSection("service")}
          >
            <Stack gap={4} style={{ flex: 1 }}>
              <Group gap={4}>
                <Text size="sm" c="dimmed">
                  Service
                </Text>
                <Text size="sm" c="red">
                  *
                </Text>
              </Group>
              <Text size="md">{formatServiceValue()}</Text>
              {errors.salonServiceId && (
                <Text size="xs" c="red">
                  {errors.salonServiceId}
                </Text>
              )}
            </Stack>
            <ActionIcon variant="transparent" style={{ marginLeft: "auto" }}>
              <IconEdit size={16} />
            </ActionIcon>
          </Group>

          {/* Duration Section */}
          <Group
            position="apart"
            className={styles.formSectionRow}
            onClick={() => openSection("duration")}
          >
            <Stack gap={4} style={{ flex: 1 }}>
              <Group gap={4}>
                <Text size="sm" c="dimmed">
                  Duration
                </Text>
                <Text size="sm" c="red">
                  *
                </Text>
              </Group>
              <Text size="md">{formatDurationValue()}</Text>
              {errors.duration && (
                <Text size="xs" c="red">
                  {errors.duration}
                </Text>
              )}
            </Stack>
            <ActionIcon variant="transparent" style={{ marginLeft: "auto" }}>
              <IconEdit size={16} />
            </ActionIcon>
          </Group>

          {/* Client Section */}
          <Group
            position="apart"
            className={styles.formSectionRow}
            onClick={() => openSection("client")}
          >
            <Stack gap={4} style={{ flex: 1 }}>
              <Group gap={4}>
                <Text size="sm" c="dimmed">
                  Client
                </Text>
                <Text size="sm" c="red">
                  *
                </Text>
              </Group>
              {formatClientValue()}
              {errors.clientUuid && (
                <Text size="xs" c="red">
                  {errors.clientUuid}
                </Text>
              )}
            </Stack>
            <ActionIcon variant="transparent" style={{ marginLeft: "auto" }}>
              <IconEdit size={16} />
            </ActionIcon>
          </Group>

          <Button
            type="submit"
            fullWidth
            size="md"
            color="teal"
            mt="xl"
            onClick={handleSubmit}
            disabled={isLoading}
            leftSection={isLoading ? <Loader size="xs" color="white" /> : null}
          >
            {isLoading ? "Saving..." : "Save"}
          </Button>
        </Stack>
      </Paper>

      {/* Section Modals */}
      <Modal
        fullScreen
        opened={activeSection === "datetime"}
        onClose={closeSection}
        withCloseButton={false}
        styles={{
          body: {
            padding: 0,
          },
        }}
      >
        <DateTimeSection
          formData={formData}
          onChange={handleFormDataChange}
          onClose={closeSection}
          errors={errors}
          validateSection={validateSection}
        />
      </Modal>

      <Modal
        fullScreen
        opened={activeSection === "staff"}
        onClose={closeSection}
        withCloseButton={false}
        styles={{
          body: {
            padding: 0,
          },
        }}
      >
        <StaffSection
          formData={formData}
          onChange={handleFormDataChange}
          onClose={closeSection}
          employees={availableStaff}
          errors={errors}
          validateSection={validateSection}
        />
      </Modal>

      <Modal
        fullScreen
        opened={activeSection === "service"}
        onClose={closeSection}
        withCloseButton={false}
        styles={{
          body: {
            padding: 0,
          },
        }}
      >
        <ServiceSection
          formData={formData}
          onChange={handleFormDataChange}
          onClose={closeSection}
          services={availableServices}
          errors={errors}
          validateSection={validateSection}
          staffName={selectedStaffName}
        />
      </Modal>

      <Modal
        fullScreen
        opened={activeSection === "duration"}
        onClose={closeSection}
        withCloseButton={false}
        styles={{
          body: {
            padding: 0,
          },
        }}
      >
        <DurationSection
          formData={formData}
          onChange={handleFormDataChange}
          onClose={closeSection}
          services={services}
          errors={errors}
          validateSection={validateSection}
        />
      </Modal>

      <Modal
        fullScreen
        opened={activeSection === "client"}
        onClose={closeSection}
        withCloseButton={false}
        styles={{
          body: {
            padding: 0,
          },
        }}
      >
        <ClientSection
          formData={formData}
          onChange={handleFormDataChange}
          onClose={closeSection}
          errors={errors}
          validateSection={validateSection}
        />
      </Modal>
    </>
  );
}

export default MobileAppointmentForm;
